import map from "lodash/map"
import find from "lodash/find"
import isEmpty from "lodash/isEmpty"
import filter from "lodash/filter"
import PropTypes from "prop-types"
import { makeStyles } from "tss-react/mui"
import Box from "component-lib/Base/components/Box"
import OutlinedInput from "component-lib/Base/components/OutlinedInput"
import InputLabel from "component-lib/Base/components/InputLabel"
import MenuItem from "component-lib/Base/components/Menu/MenuItem"
import Select from "component-lib/Base/components/Select"
import Checkbox from "component-lib/Base/components/Checkbox"
import ListItemText from "../List/ListItemText"

const useStyles = makeStyles()((theme) => ({
  root: {
    width: `100%`,
    "& .MuiSelect-select:focus": {
      background: `transparent`,
    },
    "& .MuiSelect-icon": {
      color: theme.palette.primary.main,
    },
    "& .Mui-disabled": {
      backgroundColor: theme.palette.background.disabledInputBackground,
    },
  },
  select: {
    width: `100% !important`,
  },
}))

export const CheckboxDropdown = ({
  options,
  onChange,
  value = ``,
  placeholder = ``,
  hidePlaceholder = false,
  disabled = false,
  renderValue = (val) => val?.join(", "),
  label = ``,
  labelClass = ``,
  className = ``,
  testId = ``,
}) => {
  const classes = useStyles()
  const selectedOption = find(options, (option) => {
    const optionValue = option.value || option.label
    return value.indexOf(optionValue) > -1
  })
  const selectedOptionIsExclusive = selectedOption?.isExclusive || false

  const handleChange = (e) => {
    const val = e?.target?.value || ``
    onChange(
      // On autofill we get a stringified value.
      typeof val === "string" ? val.split(",") : filter(val, (x) => x),
    )
  }

  return (
    <Box className={classes.root}>
      {label && <InputLabel className={labelClass}>{label}</InputLabel>}
      <Select
        className={className}
        style={{ width: `100%` }}
        multiple
        disabled={disabled}
        value={isEmpty(value) ? [""] : value}
        onChange={handleChange}
        input={<OutlinedInput />}
        renderValue={renderValue}
        data-testid={`CheckboxDropdown${testId ? `-${testId}` : ``}`}
      >
        {placeholder && (
          <MenuItem hidden={hidePlaceholder} disabled value="">
            {placeholder}
          </MenuItem>
        )}
        {map(options, (option) => {
          const optionLabel = option.label || option.value
          const optionValue = option.value || option.label
          const optionKey = option.key || optionValue
          const optionIsExclusive = option.isExclusive || false
          const checked = value.indexOf(optionValue) > -1

          let shouldDisable = false
          if (selectedOption) {
            if (selectedOptionIsExclusive) {
              if (optionIsExclusive) {
                shouldDisable = !checked
              } else {
                shouldDisable = true
              }
            } else {
              shouldDisable = optionIsExclusive
            }
          }

          return (
            <MenuItem
              data-testid="CheckboxDropdown-option"
              key={optionKey}
              value={optionValue}
              disabled={shouldDisable}
            >
              <Checkbox
                data-testid={`CheckboxDropdown-checkbox-${optionKey}`}
                checked={checked}
              />
              <ListItemText primary={optionLabel} />
            </MenuItem>
          )
        })}
      </Select>
    </Box>
  )
}

CheckboxDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  renderValue: PropTypes.func,
  placeholder: PropTypes.string,
  hidePlaceholder: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  labelClass: PropTypes.string,
  className: PropTypes.string,
  testId: PropTypes.string,
}

CheckboxDropdown.defaultProps = {
  value: ``,
  renderValue: (val) => val?.join(", "),
  placeholder: ``,
  hidePlaceholder: false,
  disabled: false,
  label: ``,
  labelClass: ``,
  className: ``,
  testId: ``,
}
